import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ActivityBudgetAction, ActivityBudgetGetter } from '@/store/activityBudget/activityBudget.module';
import { ActivityBudgetSchema } from '@/store/activityBudget/activityBudget.validator';
import { ActivityBudget } from '@/store/activityBudget/activityBudget.model';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ActivityBudgetFormComponent',
  components: {
    Datepicker,
    BaseModalComponent
  },
  emits: [
    'submited',
  ],
  props: {
    activityId: {
      type: Number,
    },
    provinceId: {
      type: Number,
    },
    activityBudgetId: {
      type: Number,
    }
  },
  setup: (props, context) => {
    const store = useStore();
    const isEditMode = computed(() => props.activityBudgetId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขงบประมาณค่าใช้จ่าย' : 'เพิ่มงบประมาณค่าใช้จ่าย');
    const activityBudget = computed<ActivityBudget | undefined>(() => store.getters[ActivityBudgetGetter.BY_ID](props.activityBudgetId));
    const activityBudgetForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      activityId?: number | null;
      provinceId?: number | null;
      id?: number | null;
      subject?: string | null;
      activityDate?: Date | null;
      amount?: number | null;
      remark?: string | null;
    }
    const formData = reactive<Form>({
      activityId: null,
      provinceId: null,
      id: null,
      subject: '',
      activityDate: null,
      amount: null,
      remark: ''
    });
    const validator = computed(() => ActivityBudgetSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      subject: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'subject')),
      activityDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'activityDate')),
      amount: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'amount')),
    });
    const isValid = computed(() => !errors.subject && !errors.activityDate && !errors.amount);
    const isLoading = computed<boolean>(() => store.getters[ActivityBudgetGetter.STATUS] === 'init' || store.getters[ActivityBudgetGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.activityId = props.activityId;
        formData.provinceId = props.provinceId;

        if (isEditMode.value) {
          formData.id = props.activityBudgetId;
          formData.subject = activityBudget.value?.subject;
          formData.activityDate = activityBudget.value?.activityDate;
          formData.amount = activityBudget.value?.amount;
          formData.remark = activityBudget.value?.remark;
        }
        resolve();
      })
      .then(() => activityBudgetForm.value?.show());
    };

    // hide modal
    const hide = () => {
      activityBudgetForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.activityId = null;
        formData.provinceId = null;
        formData.id = null;
        formData.subject = '';
        formData.activityDate = null;
        formData.amount = null;
        formData.remark = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const action = (isEditMode.value)
          ? ActivityBudgetAction.UPDATE
          : ActivityBudgetAction.CREATE;

        store.dispatch(action, formData)
          .then(() => {
            context.emit('submited');
            hide();
          });
      }
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      activityBudgetForm,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
    };
  },
});
